import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import {
  isChatSystemTimelineItem,
  mergeMessagesWithTimelineSystemEvents,
} from '@/components/tickets/ticket-assignment-system-messages'
import {
  SHOW_ASSIGNMENT_SYSTEM_MESSAGES_KEY,
  readShowAssignmentSystemMessages,
} from '@/components/tickets/ticket-detail-page-helpers'
import type { TimelineQuickRangeKey } from '@/components/tickets/TicketTimelinePanels'
import type { TicketDetailMessage, TicketTimelineItem, TicketTimelineResponse } from '@/types/api'

type UseTicketTimelineParams = {
  ticketId: string | null
  assignedToId?: string | null
  messages: TicketDetailMessage[]
}

export function useTicketTimeline({
  ticketId,
  assignedToId,
  messages,
}: UseTicketTimelineParams) {
  const [opened, setOpened] = useState(false)
  const [timeline, setTimeline] = useState<TicketTimelineResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fromId, setFromId] = useState<string | null>(null)
  const [toId, setToId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [selectedKind, setSelectedKind] = useState<TimelineQuickRangeKey | null>(null)
  const [showAssignmentSystemMessages, setShowAssignmentSystemMessages] = useState(
    readShowAssignmentSystemMessages,
  )
  const [assignmentTimelineItems, setAssignmentTimelineItems] = useState<TicketTimelineItem[]>([])
  const [assignmentTimelineLoading, setAssignmentTimelineLoading] = useState(false)
  const [assignmentTimelineError, setAssignmentTimelineError] = useState<string | null>(null)

  const resetSelection = useCallback(() => {
    setFromId(null)
    setToId(null)
    setSelectedIds([])
    setSelectedKind(null)
  }, [])

  useEffect(() => {
    setTimeline(null)
    setError(null)
    setOpened(false)
    resetSelection()
  }, [resetSelection, ticketId])

  const syncTimelineResponse = useCallback((response: TicketTimelineResponse) => {
    setTimeline((prev) =>
      prev && String(prev.ticket_id) === String(response.ticket_id) ? response : prev,
    )
  }, [])

  const openTimeline = useCallback(async () => {
    if (!ticketId) return
    setOpened(true)
    setLoading(true)
    setError(null)
    resetSelection()
    try {
      const res = await api.ticketTimeline(ticketId)
      setTimeline(res)
      setAssignmentTimelineItems((res.items || []).filter(isChatSystemTimelineItem))
      setAssignmentTimelineError(null)
    } catch (requestError) {
      setError(requestError instanceof ApiError ? requestError.message : 'Не удалось загрузить таймлайн')
    } finally {
      setLoading(false)
    }
  }, [resetSelection, ticketId])

  const selectItems = useCallback((ids: string[], kind: TimelineQuickRangeKey) => {
    setFromId(null)
    setToId(null)
    setSelectedIds(ids)
    setSelectedKind(kind)
  }, [])

  const pickEvent = useCallback(
    (item: TicketTimelineItem) => {
      if (!item.created_at) return
      setSelectedIds([])
      setSelectedKind(null)
      if (!fromId || toId) {
        setFromId(item.id)
        setToId(null)
        return
      }
      const fromItem = timeline?.items.find((candidate) => candidate.id === fromId)
      const fromTime = fromItem?.created_at ? new Date(fromItem.created_at).getTime() : NaN
      const nextTime = new Date(item.created_at).getTime()
      if (Number.isFinite(fromTime) && Number.isFinite(nextTime) && nextTime < fromTime) {
        setFromId(item.id)
        setToId(fromId)
        return
      }
      setToId(item.id)
    },
    [fromId, timeline?.items, toId],
  )

  const setShowAssignmentSystemMessagesPersist = useCallback((show: boolean) => {
    setShowAssignmentSystemMessages(show)
    try {
      localStorage.setItem(SHOW_ASSIGNMENT_SYSTEM_MESSAGES_KEY, show ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [])

  const loadAssignmentTimelineItems = useCallback(async () => {
    if (!ticketId) return
    setAssignmentTimelineLoading(true)
    setAssignmentTimelineError(null)
    try {
      const res = await api.ticketTimeline(ticketId)
      setAssignmentTimelineItems((res.items || []).filter(isChatSystemTimelineItem))
      syncTimelineResponse(res)
    } catch (requestError) {
      setAssignmentTimelineItems([])
      setAssignmentTimelineError(
        requestError instanceof ApiError
          ? requestError.message
          : 'Не удалось загрузить системные события таймлайна',
      )
    } finally {
      setAssignmentTimelineLoading(false)
    }
  }, [syncTimelineResponse, ticketId])

  useEffect(() => {
    setAssignmentTimelineItems([])
    setAssignmentTimelineError(null)
  }, [ticketId])

  useEffect(() => {
    if (!showAssignmentSystemMessages || !ticketId) return
    void loadAssignmentTimelineItems()
  }, [loadAssignmentTimelineItems, showAssignmentSystemMessages, ticketId])

  useEffect(() => {
    if (!showAssignmentSystemMessages || !ticketId) return
    const timer = window.setTimeout(() => {
      void loadAssignmentTimelineItems()
    }, 1500)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh only after assignee change
  }, [assignedToId])

  const visibleMessages = useMemo(() => {
    if (!showAssignmentSystemMessages || !ticketId) return messages
    return mergeMessagesWithTimelineSystemEvents(messages, assignmentTimelineItems, ticketId)
  }, [assignmentTimelineItems, messages, showAssignmentSystemMessages, ticketId])

  return {
    assignmentTimeline: {
      error: assignmentTimelineError,
      items: assignmentTimelineItems,
      loading: assignmentTimelineLoading,
      showSystemMessages: showAssignmentSystemMessages,
      toggleSystemMessages: () => {
        setShowAssignmentSystemMessagesPersist(!showAssignmentSystemMessages)
      },
      visibleMessages,
    },
    openTimeline,
    timelineModal: {
      opened,
      loading,
      error,
      timeline,
      fromId,
      toId,
      selectedIds,
      selectedKind,
      onClose: () => setOpened(false),
      onSelectItems: selectItems,
      onResetRange: resetSelection,
      onPick: pickEvent,
    },
  }
}
